import React, { useContext } from 'react';

import { ColumnEditorOptions } from 'primereact/column';

import { TagsComponentWithUnderline } from 'ui/TagsComponentWithUnderline';

import {
  ActualOperation,
  DropdownObjectType,
  Employee,
  ObjectType,
  OperationAttainment
} from '../../types/interfaces';
import type { WorkOrderTimeframe } from '../../utils/attainmentAvailability';

import { executorEditor } from './Editors';

export type ActualOperationsEditCtxValue = {
  measures: { [key: string]: DropdownObjectType };
  efforts: { [key: string]: number };
  getAttainmentsForOperation: (operation: DropdownObjectType) => ObjectType[];
  actualOperations: ActualOperation[];
  actualOperationsAttainments: OperationAttainment[];
  department: unknown;
  employees: Employee[];
  rawEmployeesData?: Employee[];
  plannedOperationsAttainments?: OperationAttainment[];
  allOperationsAttainments?: OperationAttainment[];
  operationByRowId: Record<string, string | number>;
  editorUpdateCounter: Record<string, number>;
  workOrderTimeframe?: WorkOrderTimeframe;
};

export const ActualOperationsEditCtx =
  React.createContext<ActualOperationsEditCtxValue | null>(null);

export const MeasureEditorCell: React.FC<{ options: ColumnEditorOptions }> = ({
  options
}) => {
  const ctx = useContext(ActualOperationsEditCtx);
  if (!ctx) return null;
  const key = options.rowData.operation?.key;
  const k = key !== undefined && key !== null ? String(key) : '';
  const label =
    (k && ctx.measures[k]?.label) ?? options.rowData.measure?.label ?? '';
  return <span>{label}</span>;
};

export const PlannedEffortEditorCell: React.FC<{
  options: ColumnEditorOptions;
}> = ({ options }) => {
  const ctx = useContext(ActualOperationsEditCtx);
  if (!ctx) return null;
  const opKey = options.rowData.operation?.key;
  const effortFromState =
    opKey !== undefined && opKey !== null
      ? ctx.efforts[String(opKey)]
      : undefined;
  const effortFromOperation = options.rowData.operation?.effort;
  const effort = effortFromState ?? effortFromOperation;
  return <span>{effort}</span>;
};

export const AttainmentTagsFromContext: React.FC<{
  operation: DropdownObjectType;
  rowId?: string | number;
}> = ({ operation, rowId }) => {
  const ctx = useContext(ActualOperationsEditCtx);
  if (!ctx) return null;

  const rowKey =
    rowId !== undefined && rowId !== null ? String(rowId) : '';
  const keyFromEditorState =
    rowKey !== '' ? ctx.operationByRowId[rowKey] : undefined;

  const effectiveOperation: DropdownObjectType = {
    ...(operation ?? { key: '', label: '' }),
    ...(keyFromEditorState !== undefined &&
    keyFromEditorState !== null &&
    String(keyFromEditorState) !== ''
      ? { key: keyFromEditorState }
      : {})
  };

  const opKey = effectiveOperation?.key;
  if (opKey === undefined || opKey === null || String(opKey) === '') {
    return <span className="text-color-secondary">Отсутствуют</span>;
  }

  const attainments = ctx.getAttainmentsForOperation(effectiveOperation);

  if (attainments.length === 0) {
    return <span className="text-color-secondary">Отсутствуют</span>;
  }

  const attainmentTags = attainments.map((attainment, i) => ({
    key: String(attainment.id ?? i),
    label: attainment.label
  }));

  return (
    <TagsComponentWithUnderline
      passedClassName="gap-1"
      actualOperations={ctx.actualOperations}
      tagsItems={attainmentTags}
      withoutDelete
      plannedOperationsAttainments={ctx.actualOperationsAttainments}
    />
  );
};

export const ExecutorEditCellWithCtx: React.FC<{
  options: ColumnEditorOptions;
}> = ({ options }) => {
  const ctx = useContext(ActualOperationsEditCtx);
  if (!ctx) return null;

  const rowId = options.rowData.id as string;
  const currentOperationKey =
    ctx.operationByRowId[rowId] ?? options.rowData.operation?.key;
  const updateCounter = ctx.editorUpdateCounter[rowId] ?? 0;

  return executorEditor(
    options,
    ctx.department,
    ctx.employees,
    ctx.plannedOperationsAttainments,
    ctx.rawEmployeesData,
    currentOperationKey,
    updateCounter,
    ctx.allOperationsAttainments,
    ctx.actualOperationsAttainments,
    ctx.workOrderTimeframe
  );
};
